import { getB2bConfig, getB2bCustomer, getB2bCustomerCatalog, inviteB2bCustomer, listB2bCustomerOrders, listB2bCustomers, listB2bSchemes, publishB2bScheme } from '@api/b2b';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, SheetTabStrip, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, TextInput } from '@mantine/core';
import { ArrowLeftIcon, PlusIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@hooks/useCurrentUser';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import PriceListEditor from './PriceListEditor';
import SchemeEditor from './SchemeEditor';
import { blankDraft, editingFor, type SchemeEditing } from './schemeDrafts';
import styles from './b2b.module.css';
import { Card, Field, Pill } from './shared';
import { describeScheme, errorMessage, formatDay, money, priceFallbacks, termsLabel } from './utils';

type OrderRow = Awaited<ReturnType<typeof listB2bCustomerOrders>>[number];

const InviteCard: React.FC<{ customerId: string; defaultEmail: string }> = ({ customerId, defaultEmail }) => {
    const [email, setEmail] = useState(defaultEmail);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    return (
        <Card title="Portal login" hint="One login per customer. The invitation lets them set a password and start ordering.">
            <TextInput size="xs" label="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
            <Button
                size="xs"
                variant={ButtonVariant.ACTION}
                loading={busy}
                disabled={!email.trim()}
                w="fit-content"
                onClick={() => {
                    setBusy(true);
                    setError(null);
                    inviteB2bCustomer(customerId, { email: email.trim() })
                        .then((r) => setResult(r.emailed ? `Invitation sent to ${email.trim()}.` : `Email could not be sent. Share this link: ${r.link}`))
                        .catch((e) => setError(errorMessage(e)))
                        .finally(() => setBusy(false));
                }}
            >
                Send invitation
            </Button>
            {result ? <span className={styles.cardHint}>{result}</span> : null}
            {error ? <span className={styles.error}>{error}</span> : null}
        </Card>
    );
};

/** One customer: orders, its own price list, the schemes it gets, addresses and login. */
const CustomerAccountPage: React.FC = () => {
    const { customerId = '' } = useParams<{ customerId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.role === 'ADMIN';
    const [tab, setTab] = useState('orders');
    const { data: customer, error } = useQuery({ queryKey: ['b2b-customer', customerId], queryFn: () => getB2bCustomer(customerId), enabled: !!customerId });
    const { data: orders = [] } = useQuery({ queryKey: ['b2b-customer-orders', customerId], queryFn: () => listB2bCustomerOrders(customerId), enabled: !!customerId });
    const { data: catalog } = useQuery({ queryKey: ['b2b-catalog', customerId], queryFn: () => getB2bCustomerCatalog(customerId), enabled: !!customerId });
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig });
    const { data: schemes = [] } = useQuery({ queryKey: ['b2b-schemes', false], queryFn: () => listB2bSchemes(false) });
    const { data: customers = [] } = useQuery({ queryKey: ['b2b-customers'], queryFn: listB2bCustomers });
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState<SchemeEditing | null>(null);
    const [busy, setBusy] = useState(false);
    const [schemeError, setSchemeError] = useState<string | null>(null);
    const runScheme = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setSchemeError(null);
        try {
            await action();
            await Promise.all([queryClient.invalidateQueries({ queryKey: ['b2b-schemes'] }), queryClient.invalidateQueries({ queryKey: ['b2b-catalog'] })]);
        } catch (e) {
            setSchemeError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (error) return <div className="page-shell"><span className={styles.error}>{errorMessage(error)}</span></div>;
    if (!customer) return <div className="page-shell"><span className={styles.muted}>Loading…</span></div>;

    // Drafts that would reach this customer once published: their own, or a new version of a scheme they get today.
    const running = new Set((catalog?.schemes ?? []).map((x) => x.code));
    const drafts = schemes.filter((x) => x.status === 'DRAFT' && (x.customerIds.includes(customer.customerId) || running.has(x.code)));
    const open = orders.filter((o) => !['Fulfilled', 'Cancelled'].includes(o.status));
    const ninetyDays = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
    const recentValue = orders.filter((o) => o.status !== 'Cancelled' && o.date >= ninetyDays).reduce((sum, o) => sum + o.total, 0);

    const orderColumns: DataSheetColumn<OrderRow>[] = [
        { key: 'purchaseOrderId', header: 'Order', width: 160 },
        { key: 'date', header: 'Date', width: 120, render: (v: string) => formatDay(v) },
        { key: 'status', header: 'Status', width: 160, render: (v: string) => <Pill label={v} /> },
        { key: 'customerReference', header: 'Their PO', width: 160, render: (v: string | null) => v ?? '—' },
        { key: 'total', header: 'Value', width: 130, align: 'right', render: (v: number) => money(v) },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🤝"
                title={customer.name}
                subtitle={[customer.status !== 'ACTIVE' ? customer.status : null, customer.segmentLabel, customer.gstin, customer.legalName].filter(Boolean).join(' · ')}
                actions={
                    <div className={styles.row}>
                        {isAdmin ? (
                            <Button variant={ButtonVariant.DEFAULT} onClick={() => void navigate(`/settings?section=b2bCustomers&edit=${encodeURIComponent(customer.customerId)}`)}>
                                Edit in Settings
                            </Button>
                        ) : null}
                        <Button variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={16} />} onClick={() => void navigate('/b2b/customers')}>
                            All customers
                        </Button>
                    </div>
                }
            />
            <div className={styles.layout}>
                <div className={`${styles.main} ${styles.sheet}`}>
                    <SheetTabStrip
                        tabs={[
                            { id: 'orders', label: `Sales orders (${orders.length})` },
                            { id: 'prices', label: 'Price list' },
                            { id: 'schemes', label: `Schemes (${catalog?.schemes.length ?? 0})` },
                        ]}
                        activeTab={tab}
                        onTabChange={setTab}
                    />
                    {tab === 'orders' ? (
                        <DataSheet<OrderRow>
                            bare
                            hideHeaderBar
                            data={orders}
                            columns={orderColumns}
                            getRowKey={(r) => r.purchaseOrderId}
                            onRowClick={(r) => void navigate(`/sales-orders/${encodeURIComponent(r.purchaseOrderId)}`)}
                            maxHeight="auto"
                            emptyState="No orders yet."
                        />
                    ) : null}
                    {tab === 'prices' ? (
                        <div className={styles.panelBody}>
                            <span className={styles.cardHint}>
                                Prices set here are this customer's only. Unset SKUs fall back to the {customer.segmentLabel ?? 'segment'} list, then the default list. Switch Offered off to stop selling a product to this customer.
                            </span>
                            <PriceListEditor
                                owner={`CUSTOMER:${customer.customerId}`}
                                title={`${customer.name} prices`}
                                fallbacks={priceFallbacks(`CUSTOMER:${customer.customerId}`, { key: customer.segment, label: customer.segmentLabel })}
                            />
                        </div>
                    ) : null}
                    {tab === 'schemes' ? (
                        <div className={styles.panelBody}>
                            <div className={styles.between}>
                                <span className={styles.cardHint}>Schemes running for {customer.name} today: their own, their segment's, their tags', and those for all customers.</span>
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.ACTION}
                                    leftSection={<PlusIcon size={14} />}
                                    onClick={() => setEditing({ id: null, draft: blankDraft({ appliesToAll: false, customerIds: [customer.customerId] }) })}
                                >
                                    Add scheme for {customer.name}
                                </Button>
                            </div>
                            {(catalog?.schemes ?? []).length === 0 && drafts.length === 0 ? <span className={styles.muted}>No scheme applies to this customer today.</span> : null}
                            {[...(catalog?.schemes ?? []), ...drafts].map((s) => (
                                <div key={s.id} className={styles.between}>
                                    <div className={styles.stack}>
                                        <span className={styles.fieldValue}>
                                            {s.name} {s.status === 'DRAFT' ? <Pill label="Draft, not live yet" tone="warning" /> : null}
                                        </span>
                                        <span className={styles.cardHint}>
                                            {describeScheme(s)} · {s.appliesToAll ? 'all customers' : s.customerIds.includes(customer.customerId) ? 'this customer' : 'segment or tag'}
                                        </span>
                                    </div>
                                    <div className={styles.row}>
                                        {s.status === 'DRAFT' && isAdmin ? (
                                            <Button size="xs" variant={ButtonVariant.ACTION} loading={busy} onClick={() => void runScheme(() => publishB2bScheme(s.id))}>
                                                Publish
                                            </Button>
                                        ) : null}
                                        <Button size="xs" variant={ButtonVariant.SUBTLE} loading={busy} onClick={() => void runScheme(async () => setEditing(await editingFor(s, schemes)))}>
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {schemeError ? <span className={styles.error}>{schemeError}</span> : null}
                        </div>
                    ) : null}
                </div>
                <div className={styles.rail}>
                    <Card title="Account">
                        <div className={styles.fieldGrid}>
                            <Field label="Open orders" value={open.length} />
                            <Field label="Last 90 days" value={money(recentValue)} />
                            <Field label="Last order" value={orders[0] ? formatDay(orders[0].date) : '—'} />
                            <Field label="Minimum order" value={customer.minOrderValue > 0 ? money(customer.minOrderValue) : '—'} />
                        </div>
                    </Card>
                    <Card title="Terms">
                        <Field label="Payment" value={termsLabel(customer.paymentTerms)} />
                        <Field label="PI valid for" value={config ? `${config.piValidityDays} days` : '—'} />
                    </Card>
                    <Card title="Addresses">
                        <Field label="Billing" value={customer.billingAddress || '—'} />
                        {customer.shipTos.map((s) => (
                            <Field key={s.warehouseId} label={`Ship to · ${s.name}`} value={`${s.address}${s.gstin && s.gstin !== customer.gstin ? ` · GSTIN ${s.gstin}` : ''}`} />
                        ))}
                    </Card>
                    {isAdmin ? <InviteCard customerId={customer.customerId} defaultEmail={customer.email ?? ''} /> : null}
                </div>
            </div>
            {editing ? <SchemeEditor editing={editing} config={config} customers={customers} isAdmin={isAdmin} onClose={() => setEditing(null)} /> : null}
        </div>
    );
};

export default CustomerAccountPage;
